import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { TodoTasksBoard } from '@demind-inc/core';

import {
  getOAuthAccessToken,
  integrationsApi,
  tasksApi,
  useInitTodoIntegration,
} from '../../../data-access';
import './GoogleTasksAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';

interface ExchangeCodeRes {
  accessToken?: string;
  refreshToken?: string;
  tokenExpiryDate?: string;
}

const GoogleTasksAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initTodoIntegration } = useInitTodoIntegration();

  const hasExchanged = useRef(false);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [tokenExpiryDate, setTokenExpiryDate] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [redirectDestination, setRedirectDestination] = useState<string>(Path.DASHBOARD);

  const [taskLists, setTaskLists] = useState<Partial<TodoTasksBoard>[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());

  const [isExchangeInFlight, setIsExchangeInFlight] = useState(false);
  const [isFetchingTaskLists, setIsFetchingTaskLists] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [errorState, setErrorState] = useState<{ message: string; fatal: boolean } | null>(null);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        navigate(Path.DASHBOARD);
        return;
      }

      if (hasExchanged.current) return;
      hasExchanged.current = true;

      setIsExchangeInFlight(true);
      setErrorState(null);

      try {
        let redirectTo = searchParams.get('redirectTo') || Path.DASHBOARD;
        let stateUserId = searchParams.get('userId') || '';
        let mobileFlag = searchParams.get('isMobileApp')?.includes('true') ?? false;

        if (state) {
          try {
            const stateUrl = new URL(state);
            const params = stateUrl.searchParams;
            redirectTo = params.get('redirectTo') || redirectTo;
            stateUserId = params.get('userId') || stateUserId;
            mobileFlag = params.get('isMobileApp')?.includes('true') ?? mobileFlag;
          } catch (error) {
            console.error('Failed to parse Google Tasks auth state', error);
          }
        }

        setRedirectDestination(redirectTo);
        setIsMobileApp(mobileFlag);
        setUserId(stateUserId);

        if (!stateUserId) {
          throw new Error('Missing user information.');
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'google-tasks',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.GOOGLE_TASKS_CALLBACK, searchParams)
        );

        const {
          accessToken: token,
          refreshToken: returnedRefreshToken,
          tokenExpiryDate: returnedTokenExpiryDate,
        } = (data as ExchangeCodeRes) || {};

        if (!token) {
          throw new Error('No access token returned.');
        }

        setAccessToken(token);
        setRefreshToken(returnedRefreshToken || null);
        setTokenExpiryDate(returnedTokenExpiryDate || null);
      } catch (error) {
        console.error('Error while exchanging Google Tasks code', error);
        setErrorState({
          message: "Couldn't connect to Google Tasks — try again?",
          fatal: true,
        });
        hasExchanged.current = false;
      } finally {
        setIsExchangeInFlight(false);
      }
    };

    exchange();
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let isActive = true;
    setIsFetchingTaskLists(true);

    integrationsApi
      .getOriginalTodoBoards('google-tasks', accessToken)
      .then(({ data }) => {
        if (!isActive) return;
        const fetchedTaskLists = data as Partial<TodoTasksBoard>[];
        setTaskLists(fetchedTaskLists);
        setSelectedListIds(
          new Set(
            fetchedTaskLists
              .filter((taskList) => !taskList.closed)
              .map((taskList) => taskList.originalBoardId)
              .filter(Boolean) as string[]
          )
        );

        if (!userId) {
          return;
        }

        if (!getOAuthAccessToken()) {
          return [fetchedTaskLists, null] as const;
        }

        return tasksApi
          .getTodoProjects(userId)
          .then((response) => [fetchedTaskLists, response] as const)
          .catch((error) => {
            console.warn('Failed to fetch existing Google Tasks projects', error);
            return [fetchedTaskLists, null] as const;
          });
      })
      .then((result) => {
        if (!isActive || !result) return;
        const [fetchedTaskLists, response] = result;
        const projects = (response?.data as TodoTasksBoard[]) || [];
        const googleTasksProjects = projects.filter(
          (project) => project.appFrom === 'google-tasks' && !project.closed
        );
        const existingTaskListIds = new Set(
          googleTasksProjects.map((project) => project.originalBoardId).filter(Boolean) as string[]
        );

        const listsToSelect = fetchedTaskLists
          .map((list) => list.originalBoardId)
          .filter((id) => id && existingTaskListIds.has(id)) as string[];

        if (listsToSelect.length > 0) {
          setSelectedListIds(new Set(listsToSelect));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch Google Task lists', error);
        if (!isActive) return;
        setErrorState({ message: "Couldn't load your Google Task lists.", fatal: true });
      })
      .finally(() => {
        if (!isActive) return;
        setIsFetchingTaskLists(false);
      });

    return () => {
      isActive = false;
    };
  }, [accessToken, userId]);

  const handleToggleSelectAll = () => {
    const availableListIds = taskLists
      .map((taskList) => taskList.originalBoardId)
      .filter(Boolean) as string[];

    setSelectedListIds((prev) => {
      if (!availableListIds.length) {
        return prev;
      }

      if (prev.size === availableListIds.length) {
        return new Set();
      }

      return new Set(availableListIds);
    });
  };

  const handleToggleTaskList = (taskListId: string) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);

      if (next.has(taskListId)) {
        next.delete(taskListId);
      } else {
        next.add(taskListId);
      }

      return next;
    });
  };

  const handleSync = async () => {
    if (!userId) {
      setErrorState({ message: "We're missing some account info — try connecting again.", fatal: true });
      return;
    }

    const listIds = Array.from(selectedListIds);

    if (!listIds.length) {
      return;
    }

    if (!accessToken) {
      setErrorState({ message: "We couldn't verify your login — try connecting again.", fatal: true });
      return;
    }

    try {
      setErrorState(null);
      setIsSyncing(true);
      await initTodoIntegration({
        token: accessToken,
        refreshToken: refreshToken || undefined,
        tokenExpiryDate: tokenExpiryDate || undefined,
        todoType: 'google-tasks',
        userId,
        shouldSyncTasks: true,
        skipRefresh: isMobileApp,
        originalBoardIds: listIds,
      });

      trackEventMixpanel('connect_todo_auth_done', {
        todoType: 'google-tasks',
        fromMobileApp: isMobileApp,
        taskListsSelected: listIds.length,
      });

      if (isMobileApp) {
        window.location.href = 'lifestack://todo-callback';
        return;
      }

      navigate(redirectDestination || Path.DASHBOARD);
    } catch (error) {
      console.error('Failed to trigger Google Tasks sync', error);
      setErrorState({
        message: "Couldn't start syncing those task lists.",
        fatal: false,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isExchangeInFlight && !accessToken && !errorState) {
    return (
      <div className="google-tasks-auth-callback">
        <CircularProgress />
      </div>
    );
  }

  const availableListIds = taskLists
    .map((taskList) => taskList.originalBoardId)
    .filter(Boolean) as string[];
  const allSelected =
    availableListIds.length > 0 && selectedListIds.size === availableListIds.length;
  const isIndeterminate = selectedListIds.size > 0 && !allSelected;
  const fatalError = errorState?.fatal;
  const errorMessage = errorState?.message;

  return (
    <div className="google-tasks-auth-callback">
      <div className="google-tasks-auth-callback__card">
        <Typography variant="h5" component="h1" className="google-tasks-auth-callback__title">
          Select Google Task lists
        </Typography>

        {fatalError ? (
          <div className="google-tasks-auth-callback__error">
            <Typography
              color="error"
              variant="body2"
              className="google-tasks-auth-callback__error-text"
            >
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(redirectDestination || Path.DASHBOARD)}
            >
              Back to dashboard
            </Button>
          </div>
        ) : (
          <div className="google-tasks-auth-callback__body">
            {errorMessage ? (
              <Typography
                color="error"
                variant="body2"
                className="google-tasks-auth-callback__error-text"
              >
                {errorMessage}
              </Typography>
            ) : null}

            <Typography variant="body2" className="google-tasks-auth-callback__description">
              Choose the task lists you want Lifestack to import tasks from.
            </Typography>

            {taskLists.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={allSelected}
                    onChange={handleToggleSelectAll}
                  />
                }
                label="Select all task lists"
                className="google-tasks-auth-callback__select-all"
              />
            )}

            <div className="google-tasks-auth-callback__list">
              {isFetchingTaskLists ? (
                <div className="google-tasks-auth-callback__loading">
                  <CircularProgress size={24} />
                </div>
              ) : taskLists.length ? (
                taskLists.map((taskList) => {
                  const taskListId = taskList.originalBoardId;
                  if (!taskListId) {
                    return null;
                  }

                  const isChecked = selectedListIds.has(taskListId);

                  return (
                    <label
                      key={taskListId}
                      className="google-tasks-auth-callback__list-item"
                      htmlFor={`task-list-${taskListId}`}
                    >
                      <Checkbox
                        id={`task-list-${taskListId}`}
                        checked={isChecked}
                        onChange={() => handleToggleTaskList(taskListId)}
                      />
                      <div className="google-tasks-auth-callback__list-item-info">
                        <span className="google-tasks-auth-callback__list-item-name">
                          {taskList.name || 'Untitled list'}
                        </span>
                      </div>
                    </label>
                  );
                })
              ) : (
                <Typography variant="body2" className="google-tasks-auth-callback__empty">
                  No task lists found in this Google account.
                </Typography>
              )}
            </div>

            <div className="google-tasks-auth-callback__actions">
              <Button
                variant="contained"
                color="primary"
                disabled={isSyncing || !selectedListIds.size}
                onClick={handleSync}
                className="google-tasks-auth-callback__sync-button"
              >
                {isSyncing ? (
                  <>
                    <CircularProgress
                      size={18}
                      className="google-tasks-auth-callback__button-spinner"
                    />
                    Syncing...
                  </>
                ) : (
                  `Sync ${selectedListIds.size || ''} list${selectedListIds.size === 1 ? '' : 's'}`
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleTasksAuthCallback;
